import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_REGION, buildRegionPath, useActiveRegion } from '../regions';
import { useAuth } from '../contexts/AuthContext';
import { useOpportunities } from '../contexts/OpportunityContext';
import ContractCard from '../components/ContractCard';
import ContractTable from '../components/ContractTable';
import ContractFilters from '../components/ContractFilters';
import LoadingSpinner from '../components/LoadingSpinner';
import { getComparableDueValue } from '../utils/contractFormatting';
import {
  DocumentTextIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { proBenefits } from '../utils/proBenefits';
import { opportunityMatchesFilters } from '../utils/opportunityFilters';

type TableSortField = 'title' | 'industry' | 'city' | 'release_date' | 'due_date';

const Home: React.FC = () => {
  const STALE_OVERDUE_DAYS = 7;
  const { user } = useAuth();
  const {
    opportunities,
    filteredOpportunities,
    loading,
    loadOpportunities,
    loadPremiumPreferences,
    premiumPreferences,
    viewMode,
    setViewMode,
  } = useOpportunities();
  const activeRegion = useActiveRegion() ?? DEFAULT_REGION;
  const opportunitiesPath = buildRegionPath(activeRegion, 'opportunities');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedOpportunityTypes, setSelectedOpportunityTypes] = useState<string[]>([]);
  const [selectedProcuringTypes, setSelectedProcuringTypes] = useState<string[]>([]);
  const [selectedPreBidMeeting, setSelectedPreBidMeeting] = useState<string[]>([]);
  const [selectedProcuringOrgs, setSelectedProcuringOrgs] = useState<string[]>([]);
  const [tableSortField, setTableSortField] = useState<TableSortField>('due_date');
  const [tableSortDirection, setTableSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (user?.is_premium) {
      loadOpportunities('feed');
      loadPremiumPreferences();
    } else {
      loadOpportunities('browse');
    }
  }, [user?.is_premium, loadOpportunities, loadPremiumPreferences]);

  const filteredFeed = useMemo(() => {
    return filteredOpportunities.filter((contract) =>
      opportunityMatchesFilters(contract, {
        industries: selectedIndustries,
        opportunityTypes: selectedOpportunityTypes,
        procuringTypes: selectedProcuringTypes,
        preBidMeeting: selectedPreBidMeeting,
        procuringOrganizations: selectedProcuringOrgs,
        searchTerm,
      })
    );
  }, [
    filteredOpportunities,
    searchTerm,
    selectedIndustries,
    selectedOpportunityTypes,
    selectedProcuringTypes,
    selectedPreBidMeeting,
    selectedProcuringOrgs,
  ]);

  const sortedFeed = useMemo(() => {
    const sortable = [...filteredFeed];
    const compare = (a: string | number, b: string | number) => {
      if (a < b) return tableSortDirection === 'asc' ? -1 : 1;
      if (a > b) return tableSortDirection === 'asc' ? 1 : -1;
      return 0;
    };

    const getComparableDate = (value: string | null | undefined) => {
      if (!value || value === 'TBD') return NaN;
      const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/;
      if (isoDateOnly.test(value)) {
        const [year, month, day] = value.split('-').map((part) => Number(part));
        const time = new Date(year, month - 1, day).getTime();
        return Number.isNaN(time) ? NaN : time;
      }
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? NaN : time;
    };

    const overdueBucket = (value: string | null | undefined) => {
      const timestamp = getComparableDate(value);
      if (Number.isNaN(timestamp)) return 2;
      const dueDate = new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const msPerDay = 24 * 60 * 60 * 1000;
      const dayDiff = Math.floor((dueDate.getTime() - today.getTime()) / msPerDay);
      if (dayDiff >= 0) return 0;
      if (Math.abs(dayDiff) <= STALE_OVERDUE_DAYS) return 1;
      return 2;
    };

    sortable.sort((a, b) => {
      const field = tableSortField;

      if (field === 'due_date' || field === 'release_date') {
        const aComparable = field === 'due_date' ? getComparableDueValue(a) : (a[field] as string | null | undefined);
        const bComparable = field === 'due_date' ? getComparableDueValue(b) : (b[field] as string | null | undefined);
        if (field === 'due_date' && tableSortDirection === 'asc') {
          const aBucket = overdueBucket(aComparable);
          const bBucket = overdueBucket(bComparable);
          if (aBucket !== bBucket) return aBucket - bBucket;
        }

        const aTime = getComparableDate(aComparable);
        const bTime = getComparableDate(bComparable);
        const safeATime = Number.isFinite(aTime) ? aTime : (tableSortDirection === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER);
        const safeBTime = Number.isFinite(bTime) ? bTime : (tableSortDirection === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER);
        return compare(safeATime, safeBTime);
      }

      const aValue = (a[field] ?? '').toString().toLowerCase();
      const bValue = (b[field] ?? '').toString().toLowerCase();
      return tableSortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    return sortable;
  }, [filteredFeed, tableSortField, tableSortDirection]);

  const feedOpportunities = useMemo(() => sortedFeed, [sortedFeed]);

  const feedFilters = {
    industries: selectedIndustries,
    opportunityTypes: selectedOpportunityTypes,
    procuringTypes: selectedProcuringTypes,
    preBidMeeting: selectedPreBidMeeting,
    procuringOrganizations: selectedProcuringOrgs,
    searchTerm,
  };

  const handleFeedFilterChange = (filters: Omit<typeof feedFilters, 'searchTerm'>) => {
    setSelectedIndustries(filters.industries);
    setSelectedOpportunityTypes(filters.opportunityTypes);
    setSelectedProcuringTypes(filters.procuringTypes);
    setSelectedPreBidMeeting(filters.preBidMeeting);
    setSelectedProcuringOrgs(filters.procuringOrganizations);
  };

  const handleTableSort = (field: TableSortField) => {
    if (tableSortField === field) {
      setTableSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setTableSortField(field);
      setTableSortDirection('asc');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading your dashboard..." />;
  }

  if (!user?.is_premium) {
    return (
      <div className="space-y-10 py-10">
        <div className="mx-auto max-w-4xl space-y-6 rounded-3xl border border-blue-200 bg-blue-600/95 p-10 text-blue-50 shadow-lg">
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold text-white">My Feed is a Pro feature</h1>
            <p className="text-sm text-blue-100">
              Upgrade to unlock a personalised feed, email notifications, and saved preferences tailored to your business.
            </p>
          </div>
          <div className="rounded-2xl bg-white/10 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-200">What’s included</p>
            <ul className="mt-4 space-y-2">
              {proBenefits.map((benefit) => (
                <li key={benefit.label} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white/20">
                    {benefit.icon}
                  </span>
                  <span className="leading-relaxed text-blue-50">{benefit.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/account"
              className="inline-flex items-center rounded-full bg-white px-6 py-2 text-sm font-semibold text-blue-600 shadow-sm transition hover:bg-blue-50"
            >
              Explore the Pro Plan
            </Link>
            <Link
              to={opportunitiesPath}
              className="inline-flex items-center rounded-full border border-white/60 px-6 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Browse all opportunities
            </Link>
          </div>
        </div>

        <div className="mx-auto max-w-4xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900">Browse without limits</h2>
          <p className="mt-2 text-sm text-gray-600">
            Free accounts get full access to browse, search, and filter every opportunity in Massachusetts. Upgrade when you’re
            ready for saved preferences and automated alerts.
          </p>
          <div className="mt-6">
            <Link
              to={opportunitiesPath}
              className="inline-flex items-center rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500"
            >
              Go to Opportunities
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <h1 className="text-3xl font-semibold text-gray-900">
          {user?.is_premium ? 'Your Personalised Feed' : 'Your Opportunity Dashboard'}
        </h1>
        {user?.is_premium && <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">PRO</span>}
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <p className="text-base font-semibold text-gray-900">Your Feed Snapshot</p>
            <Link
              to="/preferences"
              className="text-xs font-semibold text-blue-600 transition hover:text-blue-500"
            >
              Edit Match Preferences
            </Link>
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-[9.5rem_minmax(0,1fr)] sm:items-stretch">
            <div className="flex min-h-28 flex-col items-center justify-center rounded-xl bg-blue-50 p-4 text-center">
              <p className="text-4xl font-semibold leading-none text-blue-700">{filteredFeed.length}</p>
              <p className="mt-2 text-xs font-semibold text-blue-600">Matching opportunities</p>
            </div>
            <div className="grid content-center gap-3 text-sm">
              <PreferenceRow title="Industries" items={premiumPreferences.industries} emptyText="None saved yet" />
              <PreferenceRow
                title="Organizations"
                items={premiumPreferences.procuringOrganizations}
                emptyText="None saved yet"
              />
              <PreferenceRow
                title="Organization types"
                items={premiumPreferences.procuringTypes}
                emptyText="None saved yet"
              />
              <PreferenceRow title="Keywords" items={premiumPreferences.keywords} emptyText="None saved yet" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-base font-semibold text-gray-900">Filter Your Feed</p>
          <p className="mt-1 text-xs text-gray-500">Narrow this view without changing your saved preferences.</p>
          <div className="mt-5 grid gap-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-700" htmlFor="feed-search">
                Search
              </label>
              <input
                id="feed-search"
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search your feed..."
                className="mt-2 h-12 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <ContractFilters
              variant="compact"
              opportunitiesOverride={opportunities}
              appliedFilters={feedFilters}
              onFilterChange={handleFeedFilterChange}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500">View:</span>
          <div className="flex overflow-hidden rounded-full border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ViewColumnsIcon className="h-4 w-4" />
              Table
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
              Cards
            </button>
          </div>
        </div>
        <div className="text-sm text-gray-500">{filteredFeed.length} opportunities</div>
      </div>

      <div>
        {feedOpportunities.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 p-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-3 text-base font-semibold text-gray-900">No opportunities match these filters yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Adjust your organization or industry filters to see more personalised results.
            </p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {feedOpportunities.map((contract, index) => (
              <ContractCard key={`${contract.title}-${index}`} contract={contract} showMatchReasons />
            ))}
          </div>
        ) : (
          <ContractTable
            contracts={feedOpportunities}
            onSort={(field) => handleTableSort(field as TableSortField)}
            sortField={tableSortField}
            sortDirection={tableSortDirection}
            showMatchReasons
          />
        )}
      </div>
    </div>
  );
};

export default Home;

interface PreferenceRowProps {
  title: string;
  items: string[];
  emptyText: string;
}

const PreferenceRow: React.FC<PreferenceRowProps> = ({ title, items, emptyText }) => (
  <div className="grid min-w-0 gap-1 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-3">
    <p className="font-semibold text-purple-700">{title}</p>
    <p className="truncate text-gray-700" title={items.length ? items.join(', ') : emptyText}>
      {items.length ? items.join(', ') : emptyText}
    </p>
  </div>
);
